import { useState } from 'react';
import clsx from 'clsx';
import { Trans } from '@lingui/react/macro';
import { plural } from '@lingui/core/macro';
import { useBookmarkSelection } from '../../contexts/BookmarkSelectionContext';
import { useBatchActions } from '../../hooks/useBatchActions';
import BatchTagPopover from './BatchTagPopover';
import BatchMovePopover from './BatchMovePopover';

function MobileTopBar() {
  const { isBatchMode, selectedBookmarkIds, clearSelection } = useBookmarkSelection();
  const { batchArchive, batchDelete } = useBatchActions();
  const [showActions, setShowActions] = useState(false);
  const [showTagPopover, setShowTagPopover] = useState(false);
  const [showMovePopover, setShowMovePopover] = useState(false);

  const selectedCount = selectedBookmarkIds.size;

  const handleCancelClick = () => {
    clearSelection();
  };

  const handleBatchArchive = async () => {
    if (selectedCount === 0) return;
    await batchArchive.mutateAsync(Array.from(selectedBookmarkIds));
    clearSelection();
  };

  const handleBatchDelete = async () => {
    if (selectedCount === 0) return;
    if (
      window.confirm(
        plural(selectedCount, {
          one: 'Delete # article?',
          other: 'Delete # articles?',
        }),
      )
    ) {
      await batchDelete.mutateAsync(Array.from(selectedBookmarkIds));
      clearSelection();
    }
  };

  return (
    <div
      className={clsx(
        'px-section-x py-section-y duration-slow border-b transition-colors md:hidden',
        isBatchMode ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50',
      )}
    >
      <div className="flex-between">
        {isBatchMode ? (
          <>
            <span className="text-sm font-medium">
              <Trans>{selectedCount} selected</Trans>
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowActions(!showActions)}
                className="button-ghost"
              >
                <Trans>Actions</Trans>
              </button>
              {selectedBookmarkIds.size > 0 && (
                <button onClick={handleCancelClick} className="button-ghost">
                  <Trans>Cancel</Trans>
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="flex w-full items-center justify-between">
            <h1 className="text-lg font-semibold">
              <Trans>Articles</Trans>
            </h1>
          </div>
        )}
      </div>

      {/* Mobile Actions Drawer */}
      {isBatchMode && showActions && (
        <div className="mt-3 space-y-2 border-t border-gray-200 pt-3">
          <div className="relative">
            <button
              onClick={() => setShowTagPopover(true)}
              disabled={selectedCount === 0}
              className="button-secondary w-full disabled:opacity-50"
            >
              <Trans>Tag Articles</Trans>
            </button>
            {showTagPopover && (
              <BatchTagPopover
                bookmarkIds={Array.from(selectedBookmarkIds)}
                onClose={() => setShowTagPopover(false)}
              />
            )}
          </div>

          <div className="relative">
            <button
              onClick={() => setShowMovePopover(true)}
              disabled={selectedCount === 0}
              className="button-secondary w-full disabled:opacity-50"
            >
              <Trans>Move to Folder</Trans>
            </button>
            {showMovePopover && (
              <BatchMovePopover
                bookmarkIds={Array.from(selectedBookmarkIds)}
                onClose={() => setShowMovePopover(false)}
              />
            )}
          </div>

          <button
            onClick={handleBatchArchive}
            disabled={selectedCount === 0 || batchArchive.isPending}
            className="button-secondary w-full disabled:opacity-50"
          >
            {batchArchive.isPending ? (
              <Trans>Archiving...</Trans>
            ) : (
              <Trans>Archive</Trans>
            )}
          </button>

          <button
            onClick={handleBatchDelete}
            disabled={selectedCount === 0 || batchDelete.isPending}
            className="px-menu-item-x py-menu-item-y border-danger text-danger hover:bg-surface-danger-light w-full rounded-md border text-sm font-medium disabled:opacity-50"
          >
            {batchDelete.isPending ? <Trans>Deleting...</Trans> : <Trans>Delete</Trans>}
          </button>
        </div>
      )}
    </div>
  );
}

export default MobileTopBar;
